import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Minus, Plus, Trash2, Loader2, ShieldCheck, CreditCard } from "lucide-react";
import { useCart, formatEUR } from "../context/CartContext";
import { API } from "../constants/brand";

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const [confirmStep, setConfirmStep] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ product_id: i.car.id, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout konnte nicht gestartet werden.");
      setLoading(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setConfirmStep(false);
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#0d1320] border-l border-white/10 text-slate-100 flex flex-col"
      >
        <SheetHeader>
          <SheetTitle className="text-slate-50 text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Dein Warenkorb
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-16">
            <CreditCard size={36} className="text-slate-600" />
            <p className="text-slate-400 text-sm">Noch keine Produkte im Warenkorb.</p>
            <p className="text-slate-600 text-xs">Entdecke Reifen & Autozubehör im Shop.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-6 space-y-4 pr-1">
              {items.map(({ car, qty }) => (
                <div key={car.id} className="flex gap-4 rounded-2xl border border-white/8 bg-[#121826] p-3">
                  {car.image ? (
                    <img src={car.image} alt={car.name} className="w-24 h-16 object-cover rounded-xl" />
                  ) : (
                    <div className="w-24 h-16 rounded-xl bg-gradient-to-br from-[#101c30] to-[#0B0F17] border border-cyan-400/20 flex items-center justify-center">
                      <span className="font-mono-tech text-sm font-bold text-cyan-300">{formatEUR(car.price_eur)}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-100 truncate">{car.name}</div>
                    <div className="font-mono-tech text-xs text-cyan-300 mt-0.5">{formatEUR(car.price_eur)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        data-testid={`cart-qty-minus-${car.id}`}
                        onClick={() => setQty(car.id, qty - 1)}
                        className="w-6 h-6 rounded-md border border-white/10 flex items-center justify-center text-slate-300 hover:border-cyan-400/40"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="font-mono-tech text-sm w-5 text-center">{qty}</span>
                      <button
                        data-testid={`cart-qty-plus-${car.id}`}
                        onClick={() => setQty(car.id, qty + 1)}
                        className="w-6 h-6 rounded-md border border-white/10 flex items-center justify-center text-slate-300 hover:border-cyan-400/40"
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        data-testid={`cart-remove-${car.id}`}
                        onClick={() => remove(car.id)}
                        className="ml-auto text-slate-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-5 pb-2 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Gesamtsumme</span>
                <span className="font-mono-tech text-xl font-bold text-gradient-blue">{formatEUR(total)}</span>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-500">
                Alle Preise inkl. 19 % MwSt. Mit Klick auf „Zahlungspflichtig bestellen" gibst du eine verbindliche Bestellung ab. Es gilt unsere{" "}
                <a href="/widerruf" className="underline underline-offset-2 hover:text-cyan-300">Widerrufsbelehrung</a>.
              </p>
              {!confirmStep ? (
                <button
                  data-testid="cart-drawer-checkout-button"
                  onClick={() => setConfirmStep(true)}
                  className="w-full rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] py-3.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Jetzt bezahlen
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-slate-400 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3">
                    <ShieldCheck size={15} className="text-cyan-400 shrink-0" />
                    Sichere Zahlung über PayPal — mit PayPal-Konto oder Karte. Du wirst zur Bezahlseite weitergeleitet.
                  </div>
                  <button
                    data-testid="stripe-pay-submit-button"
                    onClick={checkout}
                    disabled={loading}
                    className="w-full rounded-xl border border-cyan-400/40 bg-cyan-400/10 py-3.5 text-sm font-bold text-cyan-300 hover:bg-cyan-400/20 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                    {loading ? "Weiterleitung…" : `Zahlungspflichtig bestellen · ${formatEUR(total)}`}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
